/**
 * URL privacy checking utilities.
 *
 * These functions help determine whether a URL points to a private/internal
 * network resource, which is important for preventing SSRF (Server-Side Request Forgery)
 * attacks.
 *
 * Uses a WHITELIST approach with ipaddr.js: only the "unicast" range is allowed.
 * This automatically blocks all non-globally-routable addresses including:
 * - Private networks (RFC 1918)
 * - Loopback addresses
 * - Link-local addresses
 * - Multicast addresses
 * - Reserved/special-purpose ranges
 * - Documentation ranges
 * - ALL IPv6 transition mechanisms (6to4, Teredo, NAT64, IPv4-mapped, etc.)
 *
 * The whitelist approach is more secure than blacklisting because any new
 * special ranges added to ipaddr.js will be blocked by default.
 */

import * as ipaddr from "ipaddr.js";

/**
 * Type for the `isIP` function from Node's `net` module.
 * Returns 0 if not an IP, 4 for IPv4, 6 for IPv6.
 */
export type IsIPFunction = (hostname: string) => number;

export function isIPAddress(hostname: string, isIP: IsIPFunction): boolean {
    return isIP(hostname) !== 0;
}

/**
 * IPv6 CIDR ranges that return "unicast" from ipaddr.js but should still be
 * blocked for SSRF prevention. These are deprecated/special ranges that
 * ipaddr.js doesn't categorize separately.
 * Format: [network, prefixLength]
 */
const BLOCKED_UNICAST_IPV6_CIDRS: Array<[string, number]> = [
    // 6bone (deprecated but should still be blocked)
    ["3ffe::", 16],
    // Site-local (deprecated but should still be blocked)
    ["fec0::", 10],
    // NAT64 local-use prefix (not covered by rfc6052 range)
    ["64:ff9b:1::", 48],
];

function isInBlockedUnicastIPv6Range(addr: ipaddr.IPv6): boolean {
    for (const [network, prefixLength] of BLOCKED_UNICAST_IPV6_CIDRS) {
        try {
            const networkAddr = ipaddr.IPv6.parse(network);
            if (addr.match(networkAddr, prefixLength)) {
                return true;
            }
        } catch {
            // Invalid network address, skip
        }
    }
    return false;
}

/**
 * Uses whitelist approach: only "unicast" range is allowed.
 */
function isPrivateIPv4(addr: ipaddr.IPv4): boolean {
    return addr.range() !== "unicast";
}

/**
 * Uses whitelist approach: only "unicast" range is allowed, and even then
 * some deprecated unicast ranges are blocked.
 */
function isPrivateIPv6(addr: ipaddr.IPv6): boolean {
    const range = addr.range();

    if (range !== "unicast") {
        return true;
    }

    if (isInBlockedUnicastIPv6Range(addr)) {
        return true;
    }

    // Check for IPv4-compatible addresses (::x.x.x.x) - deprecated but should be blocked
    // These have all zeros in the first 96 bits and non-zero in last 32 bits
    // ipaddr.js classifies these as "unicast" but they're a deprecated transition mechanism
    const parts = addr.parts;
    const isIPv4Compatible =
        parts[0] === 0 &&
        parts[1] === 0 &&
        parts[2] === 0 &&
        parts[3] === 0 &&
        parts[4] === 0 &&
        parts[5] === 0 &&
        (parts[6] !== 0 || parts[7] !== 0);

    if (isIPv4Compatible) {
        return true;
    }

    return false;
}

/**
 * Checks if an IP address is in a private/non-globally-routable range.
 *
 * This is a comprehensive check for SSRF prevention that covers all
 * non-globally-routable address ranges for both IPv4 and IPv6.
 * ALL IPv6 transition mechanisms are blocked regardless of what IPv4 they embed.
 *
 * Uses ipaddr.js as the primary parser, which handles non-canonical IPv4 forms
 * like 127.1, 0x7f.0.0.1, 0177.0.0.1, and 2130706433 that net.isIP doesn't recognize.
 *
 * @param ip - The IP address to check (without brackets for IPv6)
 */
export function isPrivateIP(ip: string): boolean {
    try {
        const addr = ipaddr.parse(ip);

        if (addr.kind() === "ipv4") {
            return isPrivateIPv4(addr as ipaddr.IPv4);
        } else {
            return isPrivateIPv6(addr as ipaddr.IPv6);
        }
    } catch {
        // Not a valid IP address in any form
        return false;
    }
}

/**
 * Checks if a hostname is considered "private" without DNS resolution.
 * Note: Bracketed IPv6 addresses (e.g., "[::1]") are not supported and will return false.
 * Note: Hostnames like "localhost" are NOT detected here - they require DNS resolution.
 *
 * @param hostname - The hostname from a URL (without brackets for IPv6)
 */
export function isPrivateHostname(hostname: string, isIP: IsIPFunction): boolean {
    if (isIPAddress(hostname, isIP)) {
        return isPrivateIP(hostname);
    }
    return false;
}

export type PrivateURLCheckResult =
    | { isPrivate: true; reason: "private-ip" | "resolved-to-private-ip" | "unresolvable" }
    | { isPrivate: false };

export type ResolveHostnameFunction = (hostname: string) => Promise<string[]>;

/**
 * This function checks the URL's hostname for direct IP addresses,
 * and performs DNS resolution for hostnames to check if they resolve to private IPs.
 */
export async function checkURLPrivacy(
    url: string,
    isIP: IsIPFunction,
    resolveHostname: ResolveHostnameFunction
): Promise<PrivateURLCheckResult> {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname;

    if (isIPAddress(hostname, isIP)) {
        if (isPrivateIP(hostname)) {
            return { isPrivate: true, reason: "private-ip" };
        }
        return { isPrivate: false };
    }

    const addresses = await resolveHostname(hostname);

    // If we can't resolve the hostname, we can't verify it's safe
    if (addresses.length === 0) {
        return { isPrivate: true, reason: "unresolvable" };
    }

    if (hasPrivateResolvedAddress(addresses)) {
        return { isPrivate: true, reason: "resolved-to-private-ip" };
    }

    return { isPrivate: false };
}

/**
 * @param addresses - Array of resolved IP addresses (without brackets for IPv6)
 */
export function hasPrivateResolvedAddress(addresses: readonly string[]): boolean {
    return addresses.some(address => isPrivateIP(address));
}
